import { ChangeDetectorRef, Directive, ElementRef, NgZone, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Observable, Subscription } from 'rxjs';
import { BaseDashboard } from '@memberjunction/ng-shared';
import { UserActivityService } from '../activity/user-activity.service';
import { AppFromDriverClass, type UserActivityApp, type UserActivityPageDescriptor } from '../activity/user-activity-event';
import { DeriveClickLabel, DeriveElementKind, ResolveClickTarget } from '../activity/click-label';
import { ShouldRecordPageView } from '../activity/page-view-guard';
import { AGUSurfaceAttachment, NextSurfaceAttachment } from './mounted-modules';

/**
 * What the base surface needs from whatever adapter owns its navigation.
 *
 * Deliberately two members. Anything more would force MAPS and the Feedback
 * Platform — whose location types and view spaces are entirely their own — to
 * converge on a shared vocabulary they have no reason to share. These two are
 * the whole of what the *lifecycle* needs.
 */
export interface AGUImmersiveSurfaceHost<TLocation> {
    /** Emits the current location on subscribe and again on every change. */
    readonly Location$: Observable<TLocation>;
    /** Recompute this surface's immersive body class from on-screen truth. */
    SyncImmersiveClass(): void;
    /** The surface hands its element over on init so modules can scroll it (see `AGUScrollportHolder`). */
    AttachScrollport(element: HTMLElement): void;
    DetachScrollport(element: HTMLElement): void;
}

/**
 * Immersive app-surface host, shared by every AGU app inside MJExplorer.
 *
 * This is the code MAPS and the Feedback Platform each hand-rolled — the
 * immersive body-class lifecycle, the visibility signal, and the zone-safe
 * location subscription. It is here rather than copied a third, fourth and fifth
 * time because the rules it encodes (detach-without-destroy, duplicate
 * instances, late destroys, SetVisible never firing) already cost one production
 * bug to get right, and N copies would have to be fixed N times.
 *
 * Subclasses supply an adapter and a mutable `location`; they own their views,
 * their data, and nothing about visibility.
 *
 * Dependencies come from `inject()` rather than constructor parameters so a
 * subclass's own constructor signature stays about its own concerns — a surface
 * should not have to thread NgZone, ChangeDetectorRef, DOCUMENT and ElementRef
 * through super() just to exist.
 *
 * It also owns activity tracking: every location change is a page view and
 * every click on a button or link inside the host is a UI click, both
 * recorded through UserActivityService.
 */
@Directive()
export abstract class BaseAGUAppSurfaceComponent<TLocation> extends BaseDashboard {
    protected readonly Zone = inject(NgZone);
    protected readonly Cdr = inject(ChangeDetectorRef);
    protected readonly Document = inject<Document>(DOCUMENT);
    protected readonly HostElement = inject<ElementRef<HTMLElement>>(ElementRef);
    /** Shared activity sink; page views and clicks from every AGU surface go through it. */
    protected readonly Activity = inject(UserActivityService);

    /** The adapter this surface navigates through. */
    protected abstract readonly Adapter: AGUImmersiveSurfaceHost<TLocation>;

    /** Current in-surface location. Subclasses seed it and templates switch on it. */
    abstract location: TLocation;

    private locationSubscription: Subscription | null = null;
    private visibilityObserver: IntersectionObserver | null = null;
    /** Where the host element stood at the last observer signal — see `NextSurfaceAttachment`. */
    private attachment: AGUSurfaceAttachment = 'never-attached';
    private clickListener: ((event: MouseEvent) => void) | null = null;
    private warnedMissingIdentity = false;

    override async ngOnInit(): Promise<void> {
        // Everything visibility-related registers BEFORE awaiting super:
        // BaseDashboard.ngOnInit is async (initDashboard + await loadData), and a
        // rejection inside it is an unhandled promise rejection that would
        // otherwise silently skip immersive setup entirely.
        //
        // Immersive mode is DERIVED state (see immersive-surface.ts): the shell
        // detaches this surface without destroying it on tab switches, can host
        // duplicate/orphaned instances of the same dashboard, and never calls
        // SetVisible (zero callers in MJ 5.51) — so a stale instance's late
        // IntersectionObserver event or destroy must never blindly clear the class
        // while another instance is visible. Every signal below recomputes the
        // global predicate instead.
        this.Adapter.SyncImmersiveClass();

        // NavigateTo can emit synchronously from inside a child's click handler.
        // Running detectChanges there would create/destroy the switched views
        // outside Angular's cycle and orphan component instances (a destroyed page
        // once kept a live subscription and swallowed a pending restore).
        // markForCheck inside the zone lets the click's own change-detection pass
        // do the swap safely.
        this.locationSubscription = this.Adapter.Location$.subscribe((location) => {
            this.Zone.run(() => {
                this.location = location;
                this.recordPageView(location);
                this.OnLocationChanged(location);
                this.Cdr.markForCheck();
            });
        });

        // The observer is a change SIGNAL only — its entries' isIntersecting is
        // deliberately ignored, because for a stale detached instance that value is
        // false while a different, visible instance owns the screen.
        this.attachment = NextSurfaceAttachment(this.attachment, this.HostElement.nativeElement.isConnected).Attachment;
        this.visibilityObserver = new IntersectionObserver(() => {
            this.Adapter.SyncImmersiveClass();
            this.TrackAttachment();
        });
        this.visibilityObserver.observe(this.HostElement.nativeElement);

        // Capture-phase so a handler that stops propagation (menus, cards that
        // swallow clicks) still gets counted. DOM-only: no template changes.
        // Guarded so a second ngOnInit on the same instance (the shell can
        // reattach a detached surface) never registers a duplicate listener.
        if (this.clickListener === null) {
            this.clickListener = (event: MouseEvent) => this.recordClick(event);
            this.HostElement.nativeElement.addEventListener('click', this.clickListener, { capture: true });
        }
        // Idempotent, so the same reattach case above is safe here too.
        this.Adapter.AttachScrollport(this.HostElement.nativeElement);

        try {
            await super.ngOnInit();
        } catch (err) {
            // Surface what BaseDashboard.ngOnInit would otherwise swallow as an
            // unhandled rejection; the surface still renders.
            console.error(`[${this.constructor.name}] BaseDashboard.ngOnInit failed`, err);
        }
    }

    override ngOnDestroy(): void {
        this.Adapter.DetachScrollport(this.HostElement.nativeElement);
        if (this.clickListener !== null) {
            this.HostElement.nativeElement.removeEventListener('click', this.clickListener, { capture: true });
            this.clickListener = null;
        }
        this.visibilityObserver?.disconnect();
        this.locationSubscription?.unsubscribe();
        // Recompute now and once more after the DOM settles: at destroy time this
        // host element may still be connected (the shell removes it afterwards),
        // and if a DIFFERENT surface instance is visible the class must survive
        // this instance's death.
        this.Adapter.SyncImmersiveClass();
        queueMicrotask(() => this.Adapter.SyncImmersiveClass());
        super.ngOnDestroy();
    }

    /**
     * Container hook for tab shows/hides. MJ 5.51 never calls it (verified: zero
     * callers) — kept as a recompute signal in case a future MJ wires it.
     */
    override SetVisible(visible: boolean): void {
        super.SetVisible(visible);
        this.Adapter.SyncImmersiveClass();
    }

    /**
     * Hook for subclasses that need to react to a location change beyond storing
     * it. Runs inside the zone, before change detection is marked. Default no-op.
     */
    protected OnLocationChanged(_location: TLocation): void {
        // no-op
    }

    /**
     * Hook for subclasses that need to refresh session-scoped state when the
     * member comes BACK to this surface after it was detached (another app tab
     * opened in between). Runs inside the zone. Default no-op.
     *
     * MJ keeps a visited dashboard cached and detached, then re-appends the same
     * instance on return without calling any lifecycle hook or `SetVisible`
     * (ng-explorer-core `tab-container.component.js` → `component-cache-manager
     * .markAsAttached`, which only replays agent tools). Without this signal a
     * surface's per-session state (visit ledger, access, memoised profile) lived
     * as long as the cache entry, not as long as the member's visit.
     */
    protected OnSurfaceReentered(): void {
        // no-op
    }

    /**
     * Folds one observer signal into `attachment` and fires `OnSurfaceReentered`
     * on a detached → attached transition. The IntersectionObserver is already
     * the surface's attach/detach signal (it fires when MJ removes and re-appends
     * the cached wrapper); `isConnected` — not `isIntersecting`, which is false
     * for a merely scrolled-away or zero-size host — is what says "detached".
     */
    private TrackAttachment(): void {
        const next = NextSurfaceAttachment(this.attachment, this.HostElement.nativeElement.isConnected);
        this.attachment = next.Attachment;
        if (next.Reentered) this.Zone.run(() => this.OnSurfaceReentered());
    }

    /**
     * Page identity for activity tracking. A string location IS the page (MAPS);
     * structured locations override this — the Research Accelerator uses
     * DescribeAppLocation, the Feedback Platform its View discriminator.
     */
    protected DescribeLocation(location: TLocation): UserActivityPageDescriptor {
        return { Page: typeof location === 'string' ? location : JSON.stringify(location), RecordID: null };
    }

    /**
     * App + dashboard identity from the MJ: Dashboards row this surface was
     * mounted from. Null (with one warning) when the shell has not set Config:
     * an unattributable event is worse than a missing one.
     */
    private activityIdentity(): { App: UserActivityApp; DashboardID: string } | null {
        const dashboard = this.Config?.dashboard;
        const app = AppFromDriverClass(dashboard?.DriverClass);
        if (!dashboard || app === null) {
            if (!this.warnedMissingIdentity) {
                this.warnedMissingIdentity = true;
                console.warn(`[${this.constructor.name}] activity not recorded: no dashboard config / unknown driver class`);
            }
            return null;
        }
        return { App: app, DashboardID: dashboard.ID };
    }

    private recordPageView(location: TLocation): void {
        if (!ShouldRecordPageView(this.HostElement.nativeElement)) return;
        const identity = this.activityIdentity();
        if (identity === null) return;
        const page = this.DescribeLocation(location);
        // A surface's seed location before its composition resolves is not a page.
        if (page.Page.trim().length === 0) return;
        this.Activity.Record({
            Kind: 'page-view',
            App: identity.App,
            DashboardID: identity.DashboardID,
            SessionID: this.Activity.SessionID,
            Page: page.Page,
            RecordID: page.RecordID,
            OccurredAt: new Date().toISOString(),
        });
    }

    private recordClick(event: MouseEvent): void {
        const target = ResolveClickTarget(event.target, this.HostElement.nativeElement);
        if (target === null) return;
        const identity = this.activityIdentity();
        if (identity === null) return;
        const page = this.DescribeLocation(this.location);
        // A click before the surface's composition resolves has no page to attribute.
        if (page.Page.trim().length === 0) return;
        this.Activity.Record({
            Kind: 'ui-click',
            App: identity.App,
            DashboardID: identity.DashboardID,
            SessionID: this.Activity.SessionID,
            Page: page.Page,
            RecordID: page.RecordID,
            OccurredAt: new Date().toISOString(),
            Label: DeriveClickLabel(target),
            ElementKind: DeriveElementKind(target),
            Href: target.tagName.toLowerCase() === 'a' ? target.getAttribute('href') : null,
        });
    }
}
